import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, History, Play, CheckCircle2 } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

const LABELS = { link: "Link to old job", new: "Import as new WC- record", review: "Needs review", exists: "Already synced", linked: "Linked", imported: "Imported", left_for_review: "Left for review", skipped: "Skipped" };

function RunCard({ title, run, testid }) {
  if (!run) return null;
  const pct = run.total_pages ? Math.min(100, Math.round(((run.next_page - 1) / run.total_pages) * 100)) : 0;
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex justify-between text-sm"><span className="font-semibold text-slate-800">{title}</span><span className="text-slate-500 capitalize" data-testid={`${testid}-status`}>{run.status}{run.status === "failed" ? " — press the button again to resume" : ""}{run.status === "running" ? ` · ${pct}%` : ""}</span></div>
      <div className="text-xs text-slate-400 mt-0.5">Started {formatDateTime(run.started_at)} by {run.started_by} · {run.orders_read || 0} orders read</div>
      {run.error && <div className="text-xs text-rose-600 mt-1">{run.error}</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        {Object.entries(run.counts || {}).map(([k, v]) => (
          <span key={k} className="text-xs rounded-full bg-slate-100 px-2.5 py-1 text-slate-700" data-testid={`${testid}-count-${k}`}>{LABELS[k] || k}: <b>{v}</b></span>
        ))}
      </div>
    </div>
  );
}

function ReviewRow({ it, onDone }) {
  const act = async (action, task_legacy_id) => {
    try { await api.post(`/woocommerce/history/items/${it.wc_number}/resolve`, { action, task_legacy_id }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const r = it.resolution;
  return (
    <div className="py-3 border-b border-slate-100" data-testid={`wch-review-${it.wc_number}`}>
      <div className="text-sm font-medium text-slate-800">Order #{it.wc_number} · {it.name || "No name"} · £{it.total} <span className="text-xs text-slate-400">{formatDateTime(it.date)} · {it.status}{it.conflict ? " · matches a job another order also matches" : ""}</span></div>
      {!!it.items?.length && <div className="text-xs text-slate-500">{it.items.join(", ")}</div>}
      <div className="flex flex-wrap gap-2 mt-2">
        {(it.candidates || []).map((c) => (
          <Button key={c.legacy_id} size="sm" variant={r?.task_legacy_id === c.legacy_id ? "default" : "outline"} onClick={() => act("link", c.legacy_id)} data-testid={`wch-link-${it.wc_number}-${c.legacy_id}`}>
            Link to job {c.legacy_id} ({formatDateTime(c.created_at)})</Button>
        ))}
        <Button size="sm" variant={r?.action === "new" ? "default" : "outline"} onClick={() => act("new")} data-testid={`wch-new-${it.wc_number}`}>Import as new</Button>
        <Button size="sm" variant={r?.action === "skip" ? "default" : "outline"} onClick={() => act("skip")} data-testid={`wch-skip-${it.wc_number}`}>Skip</Button>
      </div>
    </div>
  );
}

export default function WooHistoryPage() {
  const [st, setSt] = useState(null);
  const [rev, setRev] = useState({ items: [], total: 0 });
  const load = useCallback(() => {
    api.get("/woocommerce/history").then((r) => setSt(r.data)).catch(() => {});
    api.get("/woocommerce/history/items", { params: { decision: "review", limit: 100 } }).then((r) => setRev(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = [st?.dry_run, st?.apply].some((r) => r?.status === "running");
  useEffect(() => { if (!running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);
  const start = async (mode) => {
    if (mode === "apply" && !window.confirm("Apply the dry-run decisions now? Old jobs will gain order details and unmatched orders become WC- records.")) return;
    try { await api.post("/woocommerce/history/start", { mode }); toast.success(mode === "apply" ? "Import started" : "Dry run started"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };
  if (!st) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const dryDone = st.dry_run?.status === "done";
  return (
    <div className="p-6 lg:p-8 max-w-5xl space-y-6" data-testid="woo-history-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><History className="w-6 h-6 text-indigo-500" /> WooCommerce order history</h1>
        <p className="text-sm text-slate-500 mt-1">Reads every order from your store (read-only) and adds the order number, totals, items and payment date to the matching old web-sale job. Orders with no matching job become WC- records. Unclear matches wait here for review.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => start("dry_run")} disabled={running || st.apply?.status === "done"} variant="outline" data-testid="wch-start-dry-run"><Play className="w-4 h-4 mr-1" /> {st.dry_run ? "Re-run dry run" : "Start dry run"}</Button>
        <Button onClick={() => start("apply")} disabled={running || !dryDone || st.apply?.status === "done"} data-testid="wch-start-apply"><CheckCircle2 className="w-4 h-4 mr-1" /> Apply import</Button>
      </div>
      <RunCard title="Dry run (no changes)" run={st.dry_run} testid="wch-dry-run" />
      <RunCard title="Import" run={st.apply} testid="wch-apply" />
      {dryDone && (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wch-review-list">
          <h2 className="text-base font-bold text-slate-900">Needs review ({rev.total})</h2>
          <p className="text-xs text-slate-400 mb-2">Not linked automatically because more than one old job fits. Unresolved orders are left untouched by the import.</p>
          {!rev.items.length ? <p className="text-sm text-slate-400">Nothing to review.</p> : rev.items.map((it) => <ReviewRow key={it.wc_number} it={it} onDone={load} />)}
        </section>
      )}
    </div>
  );
}
